import { FC, MouseEvent } from "react";
import { css, styled } from "styled-components";
import { PointerRightLight, PointerRight } from "@fenderdigital/ui/Icons";
import { ContentfulNavigationItem } from "../lib/contentfulTypes";
import { colors, font } from "../lib/sharedStyles";

interface StyledLinkProps {
  $brand: string;
  $index: number;
  $isSupportNav: boolean;
  $level: number;
}

const FenderLinkStyles = css<StyledLinkProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 32px;
  ${font('Instrument Sans', 600, '16px', '22px', '0.16px')};

  ${({ $level, $isSupportNav }) => $level == 1 && !$isSupportNav && `
    font-size: 24px;
    line-height: 30px;
    letter-spacing: 0.24px;
  `}

  // No parent padding at level 3 to allow for full width images.
  // Padding needs to be applied on elements individually.
  // > span:first-of-type is targeting the title only
  ${({ $level }) => $level == 3 && `
    padding: 0;
    flex-direction: column;
    align-items: initial;
    justify-content: initial;

    > span {
      padding: 0 32px;
    }

    > span:first-of-type {
      padding-top: 12px;
      padding-bottom: 12px;
    }
  `}
`;

const JacksonLinkStyles = css<StyledLinkProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  ${font('Palanquin Dark', 600, '16px', 'normal', '0.07812rem')};

  ${({ $level }) => $level == 1 && `
    text-transform: uppercase;
    color: #aaa;
  `}

  ${({ $level }) => $level == 2 && `
    font-weight: 500;
    color: #fff;
    letter-spacing: .0125rem;
  `}

  // Shop All link
  ${({ $level, $index }) => $level == 2 && $index == 0 && `
    color: #666;
  `}
  
  ${({ $level }) => $level == 3 && `
    font-weight: 500;
    color: #939393;
    letter-spacing: normal;
    flex-direction: column;
    align-items: initial;
  `}
`;

const Link = styled.a<StyledLinkProps>`
  text-decoration: none;

  ${({ $brand }) => $brand == 'fender' && FenderLinkStyles}
  ${({ $brand }) => $brand == 'jackson' && JacksonLinkStyles};

  &:not([href]) {
    cursor: pointer;
  }
`;

const Description = styled.span`
  ${font('Instrument Sans', 400, '14px', '20px', '0.14px')}
  color: ${colors.fender.primaryDark};
  margin: 8px 0 24px;
`;

const Image = styled.img<{ $isProduct: boolean; }>`
  margin-bottom: 16px;

  ${({ $isProduct }) => $isProduct && `
    filter: drop-shadow(12px 12px 10px #ccc);
    padding: 40px 32px 16px;
  `}
`;

interface Props extends ContentfulNavigationItem {
  brand: string;
  index: number;
  isSupportNav?: boolean;
  level: number;
  onClick: (e: MouseEvent) => void;
}

const MobileMenuLink: FC<Props> = ({ 
  brand, 
  fields: { 
    asset,
    description,
    openInNewWindow,
    productImageUrl,
    subItems,
    title,
    url,
    // @ts-ignore: This can be a ContentfulNavigationRow.
    navRowTitle
  },
  index,
  isSupportNav = false,
  level,
  onClick
}) => {
  const linkTarget = (openInNewWindow) ? '_blank' : '_self';
  
  let imageUrl = '';
  let hasProductImage = false;
  // Images are not displayed in level 1.
  if (level > 1) {
    // Prefer asset field over product image.
    if (asset && asset.fields.file?.url) {
      imageUrl = `https:${asset.fields.file.url}`;
    } else if (productImageUrl) {
      imageUrl = productImageUrl;
      hasProductImage = true;
    }
  }

  const titleToRender = (navRowTitle) ? navRowTitle : title;

  const isFmicAssetsImage = (url: string): boolean => {
    return url.includes('www.fmicassets.com');
  };

  const getResizedImageUrl = (url: string): string => {
    return isFmicAssetsImage(url) ? `https://www.fender.com/cdn-cgi/image/format=png,resize=height=auto,width=500/${url}` : `${url}`;
  };

  return (
    <Link
      $brand={brand}
      $index={index}
      $isSupportNav={isSupportNav}
      $level={level}
      href={url} 
      onClick={onClick} 
      target={linkTarget}
      {
        ...(level === 1 && {'data-navigation-flyout': title}) 
        || (level === 2 && {'data-navigation-group': title}) 
        || (level === 3 && {'data-navigation-label': title})
      }
    >
      {(imageUrl) && (
        <Image src={getResizedImageUrl(imageUrl)} $isProduct={hasProductImage} />
      )}
      <span dangerouslySetInnerHTML={{ __html: titleToRender }} />
      {(level == 3 && description) && (
        <Description 
          dangerouslySetInnerHTML={{ __html: description }} 
        />
      )}
      {/* Arrow styling varies between brands. */}
      {(subItems && brand == 'jackson') && (
        <PointerRightLight fill="#fff" fontSize="16px" />
      )}
      {(subItems && brand == 'fender') && (
        <PointerRight fill={colors.fender.primaryDark} fontSize="10px" />
      )}
    </Link>
  );
}

export default MobileMenuLink;